// NEW_FILE_JUSTIFIED: New component in voiceAgents feature directory; unique multi-section form dialog
import { useReducer, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { AgentFormBasicTab } from '@/components/voiceAgents/AgentFormBasicTab';
import { AgentFormAiTab } from '@/components/voiceAgents/AgentFormAiTab';
import { AgentFormSecurityTab } from '@/components/voiceAgents/AgentFormSecurityTab';
import { AgentFormLimitsTab } from '@/components/voiceAgents/AgentFormLimitsTab';
import type {
  ICostRates,
  ICreateVoiceAgentFormData,
  IVoicePhoneNumber,
  IVoiceSipTrunk,
} from '@/@types/voiceAgents';

interface ICreateAgentDialogProps {
  // LOCAL_TYPE_JUSTIFIED: single-use props interface for this dialog component
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ICreateVoiceAgentFormData) => void;
  isSubmitting: boolean;
  phoneNumbers: IVoicePhoneNumber[];
  sipTrunks: IVoiceSipTrunk[];
  initialData?: Partial<ICreateVoiceAgentFormData>;
  title?: string;
}

type TFormAction = // LOCAL_TYPE_JUSTIFIED: reducer action type scoped to this file
  | {
      type: 'SET_FIELD';
      field: string;
      value: string | number | boolean | string[] | ICostRates | Record<string, number> | undefined;
    }
  | { type: 'RESET'; data?: Partial<ICreateVoiceAgentFormData> };

const DEFAULT_FORM: ICreateVoiceAgentFormData = {
  name: '',
  type: 'inbound',
  systemPrompt: '',
  allowedTools: [],
  llmModel: 'gpt-4o',
  requireConsent: true,
  verificationRequired: false,
  verificationMethod: 'none',
  preemptiveGeneration: false,
  turnDetectionSensitivity: 'medium',
  maxToolCallsPerCall: 10,
  maxCallDurationMs: 600000,
  maxDeniedBeforeTransfer: 3,
  maxReplySentences: 3,
  humanEscalationEnabled: false,
};

function formReducer(
  state: ICreateVoiceAgentFormData,
  action: TFormAction
): ICreateVoiceAgentFormData {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET':
      return { ...DEFAULT_FORM, ...action.data };
  }
}

export function CreateAgentDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  phoneNumbers,
  sipTrunks,
  initialData,
  title = 'Create Voice Agent',
}: ICreateAgentDialogProps) {
  const [form, dispatch] = useReducer(formReducer, { ...DEFAULT_FORM, ...initialData });

  // Reset form when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      dispatch({ type: 'RESET', data: initialData });
    }
  }, [open, initialData]);

  const setField = useCallback(
    (
      field: string,
      value: string | number | boolean | string[] | Record<string, number> | undefined
    ) => dispatch({ type: 'SET_FIELD', field, value }),
    []
  );

  const handleSubmit = useCallback(() => {
    onSubmit(form);
  }, [form, onSubmit]);

  const activePhoneNumbers = phoneNumbers.filter(p => p.isActive);
  const activeSipTrunks = sipTrunks.filter(t => t.isActive);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Configure the voice agent settings across all sections.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="ai">AI Config</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="limits">Limits</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="min-h-[520px]">
            <AgentFormBasicTab
              form={form}
              setField={setField}
              activePhoneNumbers={activePhoneNumbers}
            />
          </TabsContent>
          <TabsContent value="ai" className="min-h-[520px]">
            <AgentFormAiTab form={form} setField={setField} />
          </TabsContent>
          <TabsContent value="security" className="min-h-[520px]">
            <AgentFormSecurityTab form={form} setField={setField} />
          </TabsContent>
          <TabsContent value="limits" className="min-h-[520px]">
            <AgentFormLimitsTab form={form} setField={setField} activeSipTrunks={activeSipTrunks} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !form.name}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {initialData ? 'Save Changes' : 'Create Agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
